import { useEffect, useState } from "react";
import { Popover } from "@headlessui/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import useApiToken from "../../../hooks/useApiToken";
import { Competency, GetCompetencies } from "../../../api/interviewEvaluation.api";

type Props = {
  /** Shows the "combined across interviewers" paragraph in addition to the base formula. */
  showCombinedRule?: boolean;
  /**
   * Pass this when the caller already has the framework loaded (InterviewScorecard
   * does). Callers that don't (ScoreGrades, StructuredEvaluationDetailModal) can omit
   * it and this component fetches the weights itself.
   */
  competencies?: Competency[];
  className?: string;
};

/**
 * Explains exactly how the structured-evaluation score is derived, matching
 * InterviewScoringService.ScoreAsync/Combine on the API, including each competency's
 * actual configured weight - fetched live rather than hardcoded, since weights are
 * admin-configurable data, not constants. Only the band thresholds below are actually
 * fixed in code.
 */
export default function ScoreFormulaInfo({ showCombinedRule, competencies: provided, className }: Props) {
  const token = useApiToken();
  const [fetched, setFetched] = useState<Competency[] | null>(null);

  useEffect(() => {
    if (provided || !token) return;
    GetCompetencies(token).then((result) => {
      if (result) setFetched(result);
    });
  }, [provided, token]);

  const competencies = (provided ?? fetched ?? [])
    .filter((c) => !c.isObservationOnly)
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <Popover className={`relative inline-flex ${className ?? ""}`}>
      <Popover.Button
        type="button"
        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
        title="How is this score calculated?"
      >
        <InformationCircleIcon className="h-4 w-4" />
      </Popover.Button>

      <Popover.Panel className="absolute z-30 left-1/2 -translate-x-1/2 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-lg bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700 p-4 text-left">
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">
          How this score is calculated
        </p>

        <ol className="list-decimal list-inside space-y-2 text-xs text-gray-600 dark:text-gray-300">
          <li>
            Each scored competency (1&ndash;5) counts toward the average according to
            its weight:
            {competencies.length > 0 && (
              <ul className="mt-1 ml-1 space-y-0.5">
                {competencies.map((c) => (
                  <li key={c.id} className="flex justify-between gap-2">
                    <span>{c.name}</span>
                    <span className="font-medium text-gray-700 dark:text-gray-200">
                      {Math.round(c.weight * 100)}%
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <span className="block mt-1">
              Anything marked <strong>Not observed</strong> is left out entirely,
              rather than being treated as a middling score.
            </span>
          </li>
          <li>
            That weighted average is converted to a 0&ndash;100 score:{" "}
            <code className="px-1 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-[11px]">
              (average &minus; 1) &divide; 4 &times; 100
            </code>
          </li>
          <li>
            The 0&ndash;100 score maps to a recommendation:
            <ul className="mt-1 ml-1 space-y-0.5">
              <li>80+ &rarr; Strong Hire</li>
              <li>65&ndash;79 &rarr; Hire</li>
              <li>50&ndash;64 &rarr; Lean Hire</li>
              <li>35&ndash;49 &rarr; Lean No Hire</li>
              <li>Below 35 &rarr; No Hire</li>
            </ul>
          </li>
          <li>
            <strong>Gate:</strong> if a competency has a required minimum and it scored
            below that, the recommendation is capped at Lean No Hire &mdash; regardless
            of how high everything else is.
          </li>
          {showCombinedRule && (
            <li>
              <strong>With more than one interviewer:</strong> the combined
              recommendation is the median of everyone&rsquo;s individual result (not an
              average), and a gate breach from any one interviewer caps the combined
              result the same way.
            </li>
          )}
        </ol>
      </Popover.Panel>
    </Popover>
  );
}
